import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TemplateApiService } from '../services/template-api.service';
import { BuilderScreenBetaService } from '../services/builder-screen.service';
import { TabControlService } from '../services/tab-control.service';
import { SaveComponentsService } from '../services/save-components.service';
import { DataBindingService } from '../services/data-binding.service';
import { DataSyncService } from '../services/data-sync.service';
import { FormSyncService } from '../services/form-sync.service';
import { DataPathService } from '../services/data-path.service';
import { RxTimingService } from '../services/rx-timing.service';
import { MockDataService } from '../services/mock-data.service';
import { DataSourceConfig } from '../interfaces/data-source.models';
import { DataSourceExecutorService } from '../services/data-source-executor.service';
import { LauncherApiService } from '../services/launcher-api.service';
import { HTTPService } from 'src/app/services/http.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ButtonProps, BuilderNode } from '../interfaces/builder-screen.models';

@Component({
  selector: 'app-screen-launcher',
  templateUrl: './screen-launcher.component.html',
  styleUrls: ['./screen-launcher.component.scss'],
  standalone: false,
  // CORREÇÃO: Fornecer instâncias isoladas dos serviços
  // Cada launcher terá seu próprio estado, evitando compartilhamento entre telas
  providers: [
    BuilderScreenBetaService,
    TabControlService,
    SaveComponentsService,
    DataBindingService,
    DataSyncService,
    FormSyncService,
    DataPathService,
    RxTimingService
  ]
})
export class ScreenLauncherComponent implements OnInit {
  @Input() dataSourceConfig?: DataSourceConfig;
  @Input() initialData?: any;  // Dados passados diretamente
  
  templateId: number;
  templateName = '';
  loading = true;
  error = false;
  errorMessage = '';
  
  // Controles do modal de injeção de JSON
  isJsonModalVisible = false;
  jsonInput = '';
  isJsonValid = false;
  jsonValidationError = '';
  parsedJson: any = null;
  replaceMode = false;
  
  // Controle do FAB (Floating Action Button)
  isFabExpanded = false;
  
  // Controles do modal de Preview JSON do Fluxo
  isFlowPreviewVisible = false;
  flowButtons: { id: string; label: string; flowDataMode: string; node: BuilderNode }[] = [];
  selectedFlowButtonId = '';
  flowPreviewJson = '';

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private templateApi: TemplateApiService,
    private builderService: BuilderScreenBetaService,
    private dataBindingService: DataBindingService,
    private mockDataService: MockDataService,
    private dataSourceExecutor: DataSourceExecutorService,
    private launcherApi: LauncherApiService,
    private http: HTTPService,
    private message: NzMessageService,
    private dataSyncService: DataSyncService
  ) {}

  ngOnInit() {
    // Recebe templateId via route.data ou route.params
    // Ou busca launcher pela rota
    const routePath = this.route.snapshot.routeConfig?.path;
    
    if (routePath && !this.templateId) {
      // Carregar launcher pela rota para obter template e data source
      this.loadLauncherByRoute(routePath);
    } else {
      this.templateId = this.route.snapshot.data['templateId'] || 
                       +this.route.snapshot.params['templateId'];
      
      if (this.templateId && this.templateId > 0) {
        this.loadTemplate();
      } else {
        console.error('Template ID não fornecido ou inválido');
        this.error = true;
        this.errorMessage = 'Template ID não fornecido';
        this.loading = false;
      }
    }
  }

  /**
   * Carrega launcher pela rota e depois carrega o template e data source
   */
  private loadLauncherByRoute(routePath: string) {
    this.launcherApi.getLaunchers().subscribe({
      next: (launchers) => {
        const launcher = launchers.find(l => l.routePath === routePath);
        if (launcher) {
          this.templateId = launcher.idTemplate;
          this.dataSourceConfig = launcher.dataSourceConfig;
          this.loadTemplate();
        } else {
          console.error('Launcher não encontrado para rota:', routePath);
          this.error = true;
          this.errorMessage = 'Tela não encontrada';
          this.loading = false;
        }
      },
      error: (error) => {
        console.error('Erro ao buscar launcher:', error);
        this.error = true;
        this.errorMessage = 'Erro ao carregar tela';
        this.loading = false;
      }
    });
  }

  private loadTemplate() {
    this.templateApi.getTemplateById(this.templateId).subscribe({
      next: (template) => {
        this.templateName = template.name;
        this.builderService.applyTemplate(template);
        this.loading = false;
        
        // Carregar dados após carregar o template
        this.loadDataIfConfigured();
      },
      error: (error) => {
        console.error('Erro ao carregar template:', error);
        this.error = true;
        this.errorMessage = 'Erro ao carregar tela: ' + (error.message || 'Erro desconhecido');
        this.loading = false;
      }
    });
  }
  
  /**
   * Carrega dados se houver configuração de fonte de dados
   */
  private loadDataIfConfigured(): void {
    const routeData = this.route.snapshot.data;
    const dataConfig = routeData['dataSourceConfig'] || this.dataSourceConfig;
    
    if (dataConfig && dataConfig.type !== 'none') {
      // NOVO: Usar DataSourceExecutorService
      this.executeDataSource(dataConfig);
    } else if (this.initialData) {
      this.dataBindingService.setData(this.initialData);
    } else {
      // Sem fonte de dados configurada - inicializar com objeto vazio
      this.dataBindingService.setData({});
    }
  }
  
  /**
   * Executa o data source configurado usando DataSourceExecutorService
   */
  private executeDataSource(config: DataSourceConfig): void {
    this.dataSourceExecutor.execute(config).subscribe({
      next: (data) => {
        this.dataBindingService.setData(data);
        this.message.success('Dados carregados com sucesso!');
      },
      error: (error) => {
        console.error('Erro ao executar data source:', error);
        this.message.error('Erro ao carregar dados: ' + error.message);
      }
    });
  }
  
  
  /**
   * Abre o modal para injetar JSON customizado
   */
  openInjectJsonModal(): void {
    // Pré-preencher com os dados atuais (se houver) para facilitar edição
    const currentData = this.dataBindingService.getCurrentData();
    if (currentData && Object.keys(currentData).length > 0) {
      this.jsonInput = JSON.stringify(currentData, null, 2);
      this.validateJson();
    } else {
      this.jsonInput = '';
      this.isJsonValid = false;
    }
    
    this.isJsonModalVisible = true;
  }
  
  /**
   * Valida o JSON digitado pelo usuário
   */
  validateJson(): void {
    if (!this.jsonInput || this.jsonInput.trim() === '') {
      this.isJsonValid = false;
      this.jsonValidationError = '';
      this.parsedJson = null;
      return;
    }
    
    try {
      this.parsedJson = JSON.parse(this.jsonInput);
      this.isJsonValid = true;
      this.jsonValidationError = '';
    } catch (error: any) {
      this.isJsonValid = false;
      this.jsonValidationError = error.message || 'Erro ao validar JSON';
      this.parsedJson = null;
    }
  }
  
  /**
   * Cancela a injeção de JSON e fecha o modal
   */
  handleJsonModalCancel(): void {
    this.isJsonModalVisible = false;
    this.jsonInput = '';
    this.isJsonValid = false;
    this.jsonValidationError = '';
    this.parsedJson = null;
    this.replaceMode = false;
  }
  
  /**
   * Confirma a injeção de JSON
   */
  handleJsonModalOk(): void {
    if (!this.isJsonValid || !this.parsedJson) {
      this.message.error('JSON inválido. Por favor, corrija o JSON antes de injetar.');
      return;
    }
    
    try {
      let finalData: any;
      
      if (this.replaceMode) {
        finalData = this.parsedJson;
      } else {
        const currentData = this.dataBindingService.getCurrentData();
        finalData = this.deepMerge(currentData || {}, this.parsedJson);
      }
      
      this.dataBindingService.setData(finalData);
      
      const action = this.replaceMode ? 'substituídos' : 'mesclados';
      this.message.success(`Dados JSON ${action} com sucesso!`);
      this.isJsonModalVisible = false;
      
      this.jsonInput = '';
      this.isJsonValid = false;
      this.parsedJson = null;
      this.replaceMode = false;
      
    } catch (error: any) {
      console.error('Erro ao injetar dados:', error);
      this.message.error('Erro ao injetar dados: ' + (error.message || 'Erro desconhecido'));
    }
  }
  
  /**
   * Limpa todos os dados injetados
   */
  clearData(): void {
    this.dataBindingService.setData({});
    this.message.success('Dados limpos com sucesso!');
  }
  
  /**
   * Mescla dois objetos profundamente (deep merge)
   * IMPORTANTE: Arrays são SUBSTITUÍDOS, não mesclados
   * @param target Objeto alvo
   * @param source Objeto fonte (tem prioridade)
   * @returns Objeto mesclado
   */
  private deepMerge(target: any, source: any): any {
    const output = { ...target };
    
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (Array.isArray(source[key])) {
          output[key] = source[key];
        } else if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    
    return output;
  }
  
  /**
   * Verifica se um valor é um objeto (e não null ou array)
   */
  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }
  
  /**
   * Toggle do FAB (Floating Action Button)
   * Expande/colapsa o menu de ações
   */
  toggleFab(): void {
    this.isFabExpanded = !this.isFabExpanded;
  }
  
  // ── Preview JSON do Fluxo ──────────────────────────────────────────
  
  /**
   * Abre o modal de preview do JSON do fluxo.
   * Busca todos os botões de fluxo configurados na tela e auto-seleciona o primeiro.
   */
  openFlowJsonPreviewModal(): void {
    const nodes = this.builderService.currentState?.nodes || [];
    
    this.flowButtons = nodes
      .filter(n => {
        if (n.type !== 'button') return false;
        const props = n.props as ButtonProps;
        return props.actionType === 'flow' 
            || props.actionType === 'executeFlow'
            || props.action === 'executeFlow';
      })
      .map(n => {
        const props = n.props as ButtonProps;
        const label = props.text || props.label || `Botão ${n.id}`;
        const flowDataMode = props.flowDataMode || 'full';
        return { id: n.id, label, flowDataMode, node: n };
      });
    
    if (this.flowButtons.length > 0) {
      this.selectedFlowButtonId = this.flowButtons[0].id;
      this.updateFlowPreview();
    } else {
      this.selectedFlowButtonId = '';
      this.flowPreviewJson = '';
    }
    
    this.isFlowPreviewVisible = true;
  }
  
  /**
   * Retorna o botão de fluxo selecionado.
   */
  getSelectedFlowButton(): { id: string; label: string; flowDataMode: string; node: BuilderNode } | undefined {
    return this.flowButtons.find(b => b.id === this.selectedFlowButtonId);
  }
  
  /**
   * Atualiza o JSON de preview conforme o botão selecionado e seu flowDataMode.
   * Replica a mesma lógica de resolução de dados do canvas.component.ts.
   */
  updateFlowPreview(): void {
    const btn = this.getSelectedFlowButton();
    if (!btn) {
      this.flowPreviewJson = '';
      return;
    }
    
    let data: any = null;
    
    if (btn.flowDataMode === 'selectedItem') {
      // Mesma lógica de executeFlowWithSelectedItem
      const mainRowFromStore = this.dataSyncService.getMainRowFromStore();
      if (mainRowFromStore != null) {
        data = mainRowFromStore;
      } else {
        // Fallback: item atual
        const selectedItem = this.dataSyncService.getCurrentItem();
        const currentIndex = this.dataSyncService.getCurrentIndex();
        if (selectedItem && currentIndex !== -1) {
          data = selectedItem;
        }
      }
    } else {
      // Modo 'full' — mesma lógica de executeFlowWithFullData
      const directData = this.dataBindingService.getCurrentData();
      const completeData = this.dataSyncService.getCompleteData();
      data = completeData || directData;
    }
    
    if (data) {
      this.flowPreviewJson = JSON.stringify(data, null, 2);
    } else {
      this.flowPreviewJson = '';
    }
  }
  
  /**
   * Copia o JSON de preview para o clipboard.
   */
  copyFlowJson(): void {
    if (!this.flowPreviewJson) {
      this.message.warning('Nenhum JSON para copiar');
      return;
    }
    
    navigator.clipboard.writeText(this.flowPreviewJson).then(() => {
      this.message.success('JSON copiado para o clipboard!');
    }).catch(() => {
      // Fallback para navegadores mais antigos
      const textarea = document.createElement('textarea');
      textarea.value = this.flowPreviewJson;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.message.success('JSON copiado para o clipboard!');
    });
  }
}

